import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HTMLMessage, InfoMessage } from '../models';
import { useStores } from '../stores';
import { MESSAGE_TYPE_RECEIVED } from '../utilities';
import useConfig from './useConfig';

export default function useQuestions(scrollbarRef) {
  const {
    message: { hotTopics, popularQuestions },
    carousel
  } = useConfig();
  const { chat, chatWindow, bot, network, ui } = useStores();
  const isSentHotTopics = useRef(false);
  const isSentPopularQuestins = useRef(false);
  const [isHotTopicsReady, setHotTopicsReady] = useState(false);
  const { t } = useTranslation();

  // Hot Topics
  useEffect(() => {
    if (carousel && carousel.enabled) {
      return () => {};
    }

    let timeoutId;
    const isChatReady = !chatWindow.isClosed && network.isOnline && !network.isSlowConnection;

    if (
      isChatReady &&
      hotTopics.enabled &&
      bot.hotTopics.length &&
      chat.isWelcomeMessage &&
      !isSentHotTopics.current
    ) {
      timeoutId = setTimeout(() => {
        if (ui.category.isCleared) {
          chat.addMessage(
            new HTMLMessage({
              content: t('headerMenu.welcomeMessage', {
                escapeValue: false,
                categoryName: ui.category.id ? t(ui.category.name) : null
              }),
              type: MESSAGE_TYPE_RECEIVED
            })
          );
          ui.category.isCleared = false;
        }
        chat.addMessage(
          new InfoMessage({
            title: 'hotTopics',
            content: bot.hotTopics,
            type: MESSAGE_TYPE_RECEIVED
          })
        );

        isSentHotTopics.current = true;
        setHotTopicsReady(true);
        chat.isWelcomeMessage = false;
        scrollbarRef.scrollToTop();
      }, hotTopics.timeout);
    }

    if (chatWindow.isClosed) {
      isSentHotTopics.current = false;
    }

    return () => {
      if (chat.messages.length === 0) {
        isSentHotTopics.current = false;
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    chat,
    chat.isWelcomeMessage,
    chat.messages.length,
    chatWindow.isClosed,
    bot.hotTopics,
    hotTopics,
    network.isOnline,
    network.isSlowConnection,
    scrollbarRef,
    carousel,
    ui.category.isCleared,
    ui.category.id,
    ui.category.name,
    t
  ]);

  // Popular Questions
  useEffect(() => {
    if (carousel && carousel.enabled) {
      return () => {};
    }
    let timeoutId;
    const isChatReady = !chatWindow.isClosed && network.isOnline && !network.isSlowConnection;

    const animationTimeout =
      hotTopics.enabled && bot.hotTopics.length
        ? popularQuestions.timeout + 200
        : popularQuestions.timeout;

    if (
      isChatReady &&
      popularQuestions.enabled &&
      bot.topQuestions.length &&
      (isHotTopicsReady || chat.isWelcomeMessage) &&
      !isSentPopularQuestins.current
    ) {
      timeoutId = setTimeout(() => {
        if (ui.category.isCleared) {
          chat.addMessage(
            new HTMLMessage({
              content: t('headerMenu.welcomeMessage', {
                escapeValue: false,
                categoryName: ui.category.id ? t(ui.category.name) : null
              }),
              type: MESSAGE_TYPE_RECEIVED
            })
          );
          ui.category.isCleared = false;
        }
        chat.addMessage(
          new InfoMessage({
            title: 'popularQuestions',
            content: bot.topQuestions,
            type: MESSAGE_TYPE_RECEIVED
          })
        );

        isSentPopularQuestins.current = true;
        chat.isWelcomeMessage = false;

        if (isHotTopicsReady) {
          setHotTopicsReady(false);
        }
        scrollbarRef.scrollToTop();
      }, animationTimeout);
    }

    if (chatWindow.isClosed) {
      isSentPopularQuestins.current = false;
    }

    return () => {
      if (chat.messages.length === 0) {
        isSentPopularQuestins.current = false;
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    bot.hotTopics.length,
    bot.topQuestions,
    chat,
    chat.isWelcomeMessage,
    chat.messages.length,
    chatWindow.isClosed,
    network.isOnline,
    network.isSlowConnection,
    popularQuestions,
    hotTopics.enabled,
    scrollbarRef,
    carousel,
    isHotTopicsReady,
    t,
    ui.category.id,
    ui.category.name,
    ui.category.isCleared
  ]);
}
